import React, { useState } from 'react'

import { useParams, useNavigate } from 'react-router-dom'
import { isAfter } from 'date-fns'
import { useTheme } from 'styled-components'

import { useSharedDates } from '@contexts/sharedDates'
import { useAddBarrierComment } from '@hooks/barriers/comments'
import { useFindBarriers } from '@hooks/barriers/find'
import { useDate } from '@hooks/dates'
import { useRoles } from '@hooks/roles'
import { useFindTask } from '@hooks/task/find'
import { IBarrierComment } from '@interfaces/task/barrier'
import { CommentsModal } from '@screens/Task/components/Comments'
import { NoContent } from '@screens/Task/components/NoContent'
import { Section } from '@screens/Task/components/Section'

import { BarrierCard } from './Card'

export function BarriersSection() {
  const theme = useTheme()
  const { taskId } = useParams<{ taskId: string }>()

  const navigate = useNavigate()

  const { data: rawTask } = useFindTask(Number(taskId))

  const { mutateAsync: postBarrierComment } = useAddBarrierComment()
  const { getWeekRange } = useDate()
  const { selectedDate } = useSharedDates()
  const { readOnlyUserRoles, isUserAllowed } = useRoles()
  const isReadOnlyUser = isUserAllowed(readOnlyUserRoles)

  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false)
  const [barrierComments, setBarrierComments] = useState<IBarrierComment[]>([])
  const [barrierId, setBarrierId] = useState<number | undefined>()

  const task = rawTask ?? ({} as NonNullable<typeof rawTask>)
  const { data: rawBarriers, isLoading } = useFindBarriers(task.id)

  const barriers = rawBarriers ?? []

  const { weekEnd } = getWeekRange(new Date())
  const isSelectedDateAhead = isAfter(selectedDate, weekEnd)

  const hasBarriers = barriers.length > 0

  const barrierActions = [
    {
      title: 'Add Barrier',
      isDisabled: isSelectedDateAhead || isReadOnlyUser,
      onPress: () => navigate(`/task/${taskId}/add-barrier`),
    },
  ]

  function getBarrierComments(comments: IBarrierComment[]) {
    setBarrierComments(comments)
    setIsCommentsModalOpen(true)
  }

  function handleBarrierComment(comment: string) {
    if (barrierId) {
      postBarrierComment({
        comment,
        barrierId,
        taskId: Number(taskId),
      })
    }
  }

  function handleNavigateEditBarrier(barrierId: number) {
    navigate(`/task/${taskId}/edit-barrier/${barrierId}`)
  }

  return (
    <Section title="Barriers" actions={barrierActions}>
      {isLoading ? (
        <NoContent
          backgroundColor={theme.colors.foregroundPaper}
          type="loading"
        />
      ) : (
        <>
          {hasBarriers ? (
            <div style={{ display: 'flex', overflowX: 'auto', gap: '8px' }}>
              {barriers.map((item) => (
                <BarrierCard
                  onCommentsPress={() => {
                    if (item.comments) getBarrierComments(item.comments)
                    setBarrierId(item.id)
                  }}
                  key={item.id}
                  barrier={item}
                  onEditBarrier={() => handleNavigateEditBarrier(item.id)}
                  isReadOnly={isReadOnlyUser}
                />
              ))}
            </div>
          ) : (
            <NoContent
              backgroundColor={theme.colors.foregroundPaper}
              type="barriers"
              message="No barriers reported. You are good to go!"
            />
          )}

          <CommentsModal
            openModal={isCommentsModalOpen}
            comments={barrierComments}
            handleComment={handleBarrierComment}
            onClose={() => setIsCommentsModalOpen(false)}
            isReadOnly={isReadOnlyUser}
          />
        </>
      )}
    </Section>
  )
}
